// src/pages/Forum.js - PUBLIC VIEW FORUM WITH VIEW TRACKING FIXED
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Send,
  MessageCircle,
  Search,
  Filter,
  Clock,
  Reply,
  ThumbsUp,
  Eye,
  AlertCircle,
  Loader,
  User as UserIcon,
  RefreshCw,
  Archive,
  Calendar,
  X,
  MessageSquarePlus,
  TrendingUp,
  Lock,
  LogIn,
  Shield,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Users,
  UserCheck,
  UserCog,
  Globe,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Forum() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Message states
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  
  // Filter and search states
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [replyTo, setReplyTo] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Archive states
  const [showArchives, setShowArchives] = useState(false);
  const [archives, setArchives] = useState([]);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [archiveMessages, setArchiveMessages] = useState([]);
  const [forumStats, setForumStats] = useState(null);

  // Scroll states
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [userScrolledAway, setUserScrolledAway] = useState(false);

  // ===== NEW: VIEW TRACKING STATE =====
  const [trackingViews, setTrackingViews] = useState({});

  // ===== NEW: ONLINE USERS EXPAND STATE =====
  const [showOnlineUsers, setShowOnlineUsers] = useState(false);

  const [viewerStats, setViewerStats] = useState({
    totalViewers: 0,
    loggedInViewers: 0,
    guestViewers: 0,
    updatedAt: null
  });

  const trackViewerPresence = async () => {
    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/forum/track-viewer`, {
        method: 'POST',
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.viewerStats) {
          setViewerStats(data.viewerStats);
        }
      }
    } catch (error) {
      console.error('Error tracking viewer presence:', error);
    }
  };

  const fetchViewerStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/forum/viewer-stats`);
      if (response.ok) {
        const data = await response.json();
        if (data.viewerStats) {
          setViewerStats(data.viewerStats);
        }
      }
    } catch (error) {
      console.error('Error fetching viewer stats:', error);
    }
  };

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Fetch all messages (available to everyone)
  const fetchMessages = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const response = await fetch(`${API_URL}/api/forum/messages`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Failed to load messages');
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  // Fetch online users
  const fetchOnlineUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/forum/online-users`);
      if (response.ok) {
        const data = await response.json();
        setOnlineUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching online users:', error);
    }
  };

  // Fetch forum statistics
  const fetchForumStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/forum/stats`);
      if (response.ok) {
        const data = await response.json();
        setForumStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch available archives
  const fetchArchives = async () => {
    try {
      const response = await fetch(`${API_URL}/api/forum/archives`);
      if (response.ok) {
        const data = await response.json();
        setArchives(data.archives || []);
      }
    } catch (error) {
      console.error('Error fetching archives:', error);
    }
  };

  // Load specific archive
  const loadArchive = async (year) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/forum/archives/${year}`);
      if (response.ok) {
        const data = await response.json();
        setArchiveMessages(data.messages || []);
        setSelectedArchive(data);
      }
    } catch (error) {
      console.error('Error loading archive:', error);
      setError('Failed to load archive');
    } finally {
      setLoading(false);
    }
  };

  // Update user presence
  const updatePresence = async () => {
    if (!user) return;
    try {
      await fetch(`${API_URL}/api/forum/presence`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ userId: user._id, name: user.name })
      });
    } catch (error) {
      console.error('Error updating presence:', error);
    }
  };

  // Track message view
  const trackMessageView = async (messageId) => {
    if (!messageId) return;

    if (trackingViews[messageId]) return;

    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_URL}/api/forum/messages/${messageId}/view`,
        {
          method: 'POST',
          headers,
        }
      );

      if (response.ok) {
        setTrackingViews(prev => ({ ...prev, [messageId]: true }));
        await fetchMessages(false);
      }
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  // Setup polling intervals
  useEffect(() => {
    // Everyone can fetch messages
    fetchMessages();
    
    if (user) {
      updatePresence();
    }
    fetchOnlineUsers();
    fetchForumStats();

    const presenceInterval = user ? setInterval(updatePresence, 30000) : null;
    const messageInterval = setInterval(() => fetchMessages(false), 5000);
    const userInterval = setInterval(fetchOnlineUsers, 10000);

    return () => {
      if (presenceInterval) clearInterval(presenceInterval);
      if (messageInterval) clearInterval(messageInterval);
      clearInterval(userInterval);
    };
  }, [user]);

  useEffect(() => {
    // Initial tracking
    trackViewerPresence();
    fetchViewerStats();
    
    // Set up interval for tracking (every 25 seconds)
    const viewerInterval = setInterval(() => {
      trackViewerPresence();
    }, 25000);
    
    // Set up interval for stats refresh (every 10 seconds)
    const statsInterval = setInterval(() => {
      fetchViewerStats();
    }, 10000);
    
    return () => {
      clearInterval(viewerInterval);
      clearInterval(statsInterval);
    };
  }, [user]);

  // Auto-scroll when messages update
  useEffect(() => {
    if (messagesContainerRef.current) {
      const rect = messagesContainerRef.current.getBoundingClientRect();
      const isContainerVisible = rect.top < window.innerHeight && rect.bottom > 0;

      if (!userScrolledAway && isAtBottom && isContainerVisible) {
        scrollToBottom();
      }
    }
  }, [messages, userScrolledAway, isAtBottom]);

  // Send new message (only for logged in users)
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    if (!user) {
      setError('Please login to send messages');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    setSending(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/forum/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          message: newMessage.trim(),
          replyTo: replyTo?._id
        })
      });

      if (response.ok) {
        setNewMessage('');
        setReplyTo(null);
        setUserScrolledAway(false);
        await fetchMessages(false);
        setTimeout(() => scrollToBottom(), 100);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Like message (only for logged in users)
  const handleLike = async (messageId) => {
    if (!user) {
      setError('Please login to like messages');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/forum/messages/${messageId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        await fetchMessages(false);
      }
    } catch (error) {
      console.error('Error liking message:', error);
    }
  };

  // Manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMessages(false);
    await Promise.all([fetchOnlineUsers(), fetchForumStats()]);
    setRefreshing(false);
  };

  // Format time display
  const formatTime = (date) => {
    const now = new Date();
    const msgDate = new Date(date);
    const diffMs = now - msgDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return msgDate.toLocaleDateString();
  };

  // Filter messages
  const displayMessages = selectedArchive ? archiveMessages : messages;
  const filteredMessages = displayMessages.filter(msg => {
    if (searchQuery && !msg.message.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !msg.userName.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    if (filter === 'my-messages' && user && msg.userId !== user.email) {
      return false;
    }

    if (filter === 'today' && !selectedArchive) {
      const today = new Date().setHours(0, 0, 0, 0);
      const msgDate = new Date(msg.createdAt).setHours(0, 0, 0, 0);
      if (msgDate !== today) return false;
    }

    return true;
  });

  // Make sure messages show oldest at top, newest at bottom
  const orderedMessages = [...filteredMessages].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );

  // Stats cards data
  const stats = [
    {
      value: selectedArchive ? selectedArchive.messageCount : messages.length,
      label: selectedArchive ? 'Archived Messages' : 'Total Messages',
      icon: <MessageCircle className="w-6 h-6" />,
      color: 'blue'
    },
    {
      value: onlineUsers.length,
      label: 'Online Users',
      icon: <Users className="w-6 h-6" />,
      color: 'green'
    },
    {
      value: user ? 'Active' : 'Guest',
      label: 'Your Status',
      icon: user ? <UserIcon className="w-6 h-6" /> : <Lock className="w-6 h-6" />,
      color: 'purple'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ===== HERO HEADER ===== */}
        <div className="mb-6 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 rounded-2xl p-8 text-white shadow-2xl overflow-hidden relative">
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            />
          </div>

          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <MessageSquarePlus className="w-10 h-10" />
              <div>
                <h1 className="text-3xl md:text-4xl font-bold">
                  Community Forum
                  {selectedArchive && (
                    <span className="ml-3 text-lg opacity-90">
                      (Archive: {selectedArchive.year})
                    </span>
                  )}
                </h1>
                <p className="text-blue-100 mt-1">
                  {user ? 'Connect with our community in real-time' : 'Browse community discussions. Login to participate.'}
                </p>
              </div>
            </div>
            {!user && (
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white rounded-lg hover:bg-white/30 transition shadow-lg font-semibold whitespace-nowrap"
              >
                <LogIn className="w-5 h-5" />
                Login to Join
              </button>
            )}
            {user && (
              <button
                onClick={() => {
                  setShowArchives(!showArchives);
                  if (!showArchives) fetchArchives();
                }}
                className="flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white rounded-lg hover:bg-white/30 transition shadow-lg font-semibold whitespace-nowrap"
              >
                <Archive className="w-5 h-5" />
                Archives
              </button>
            )}
          </div>

          {/* Forum Stats Banner */}
          {forumStats && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                <div className="text-3xl font-bold">{forumStats.totalMessages}</div>
                <div className="text-sm opacity-90 mt-1">Messages this year</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                <div className="text-3xl font-bold">{forumStats.totalArchivedMessages || 0}</div>
                <div className="text-sm opacity-90 mt-1">Total archived</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                <div className="text-3xl font-bold">{forumStats.totalResets || 0}</div>
                <div className="text-sm opacity-90 mt-1">Yearly resets</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                <div className="text-3xl font-bold">{forumStats.currentYear}</div>
                <div className="text-sm opacity-90 mt-1">Current year</div>
              </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* ===== UPDATED ONLINE USERS SIDEBAR WITH VIEWER STATS ===== */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 md:p-6 sticky top-24">
              {/* Header with toggle button */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75"></div>
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-sm md:text-base">
                    Live Viewers
                  </h3>
                </div>
                
                <button
                  onClick={() => setShowOnlineUsers(!showOnlineUsers)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs md:text-sm font-medium rounded-lg hover:bg-green-200 dark:hover:bg-green-800/40 transition-all duration-200"
                >
                  {showOnlineUsers ? (
                    <>
                      <ChevronUp className="w-3 h-3 md:w-4 md:h-4" />
                      <span className="hidden sm:inline">Hide</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3 md:w-4 md:h-4" />
                      <span className="hidden sm:inline">View</span>
                    </>
                  )}
                  <span className="ml-1">({onlineUsers.length})</span>
                </button>
              </div>

              {/* Viewer Statistics */}
              <div className="space-y-4 mb-6">
                {/* Total Viewers */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Total Viewing Now
                      </span>
                    </div>
                    <div className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold rounded-full">
                      LIVE
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white text-center">
                    {viewerStats.totalViewers}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                    People currently viewing
                  </div>
                </div>

                {/* Viewer Breakdown */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Logged In Viewers */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2 mb-1">
                      <UserCheck className="w-3 h-3 text-green-600 dark:text-green-400" />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        Members
                      </span>
                    </div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white">
                      {viewerStats.loggedInViewers}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Logged in
                    </div>
                  </div>

                  {/* Guest Viewers */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center gap-2 mb-1">
                      <UserCog className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        Guests
                      </span>
                    </div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white">
                      {viewerStats.guestViewers}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Exploring
                    </div>
                  </div>
                </div>

                {/* Conversion Opportunity */}
                {!user && viewerStats.guestViewers > 0 && (
                  <div className="bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      <span className="text-sm font-bold text-orange-800 dark:text-orange-300">
                        Join the Conversation!
                      </span>
                    </div>
                    <p className="text-xs text-orange-700 dark:text-orange-400 mb-2">
                      {viewerStats.guestViewers} {viewerStats.guestViewers === 1 ? 'person is' : 'people are'} viewing this page. Be the first to join!
                    </p>
                    <button
                      onClick={() => navigate('/signup')}
                      className="w-full px-3 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white text-xs font-bold rounded-lg hover:from-orange-600 hover:to-yellow-600 transition shadow-md"
                    >
                      Join Now & Chat
                    </button>
                  </div>
                )}
              </div>

              {/* Collapsible Online Users List */}
              {showOnlineUsers && (
                <div className="transition-all duration-300 ease-in-out">
                  <div className="mb-3">
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium mb-2">
                      Online Members List
                    </h4>
                  </div>
                  
                  <div className="space-y-3 max-h-64 md:max-h-80 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                    {onlineUsers.length === 0 ? (
                      <div className="text-center py-6">
                        <Users className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No users online
                        </p>
                      </div>
                    ) : (
                      onlineUsers.slice(0, user ? undefined : 20).map((onlineUser, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all group"
                        >
                          <div className="relative flex-shrink-0">
                            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm md:text-lg shadow-md">
                              {onlineUser.name?.charAt(0).toUpperCase()}
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {onlineUser.name}
                              {user && onlineUser.userId === user.email && (
                                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full">You</span>
                              )}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  Active
                                </span>
                              </div>
                              <div className="w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {onlineUser.lastSeen ? 
                                  `Last seen ${formatTime(onlineUser.lastSeen)}` : 
                                  'Now'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Mobile-Friendly Toggle for Non-Logged In Users */}
              {!user && (
                <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                      Join the conversation
                    </p>
                    <Users className="w-4 h-4 text-blue-500" />
                  </div>
                  <p className="text-xs text-blue-800 dark:text-blue-400 mb-3">
                    {onlineUsers.length > 0 
                      ? `${onlineUsers.length} community members are chatting right now!` 
                      : 'Be the first to join our community!'}
                  </p>
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition shadow-md font-medium flex items-center justify-center gap-2 text-sm"
                  >
                    <LogIn className="w-4 h-4" />
                    Login to Join Chat
                  </button>
                </div>
              )}

              {/* Mobile Toggle Button for Small Screens */}
              <div className="lg:hidden mt-4">
                <button
                  onClick={() => setShowOnlineUsers(!showOnlineUsers)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                >
                  {showOnlineUsers ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      <span className="text-sm font-medium">Hide Members List</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      <span className="text-sm font-medium">View All Members ({onlineUsers.length})</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* ===== MAIN CHAT AREA ===== */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-[600px]">
              {/* Filters and Search */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 flex-shrink-0 space-y-3">
                {selectedArchive && (
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Archive className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      <span className="text-sm font-medium text-purple-800 dark:text-purple-300">
                        Viewing archive from {selectedArchive.year} ({selectedArchive.messageCount} messages)
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedArchive(null);
                        setArchiveMessages([]);
                      }}
                      className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  {/* Search */}
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search messages..."
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Filter Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {['all', 'my-messages', 'today'].map(filterOption => (
                      filterOption === 'my-messages' && (!user || selectedArchive) ? null :
                        filterOption === 'today' && selectedArchive ? null :
                          <button
                            key={filterOption}
                            onClick={() => setFilter(filterOption)}
                            className={`px-4 py-3 text-sm font-medium rounded-lg transition ${
                              filter === filterOption
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {filterOption === 'all' ? 'All' : filterOption === 'my-messages' ? 'My Messages' : 'Today'}
                          </button>
                    ))}
                    {!selectedArchive && (
                      <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="px-4 py-3 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition disabled:opacity-50"
                        title="Refresh"
                      >
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900"
                onScroll={(e) => {
                  const element = e.target;
                  const bottom = element.scrollHeight - element.scrollTop - element.clientHeight;
                  const isNearBottom = bottom < 100;
                  setIsAtBottom(isNearBottom);

                  if (!isNearBottom && element.scrollTop < element.scrollHeight - element.clientHeight - 200) {
                    setUserScrolledAway(true);
                  } else if (isNearBottom) {
                    setUserScrolledAway(false);
                  }
                }}
              >
                {loading ? (
                  // ===== LOADING STATE =====
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">Loading messages...</p>
                    </div>
                  </div>
                ) : filteredMessages.length === 0 ? (
                  // ===== NO MESSAGES STATE =====
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <MessageCircle className="w-20 h-20 text-gray-400 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Messages</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {searchQuery || filter !== 'all'
                        ? 'No messages found matching your criteria'
                        : selectedArchive
                        ? 'No archived messages available'
                        : 'Be the first to start the conversation!'}
                    </p>
                  </div>
                ) : (
                  // ===== MESSAGES VIEW (FOR ALL USERS) =====
                  orderedMessages.map((msg) => {
                    // TRACK VIEW WHEN MESSAGE RENDERS
                    if (!trackingViews[msg._id] && msg._id) {
                      trackMessageView(msg._id);
                    }

                    return (
                      <div
                        key={msg._id}
                        className={`flex gap-3 ${user && msg.userId === user.email ? 'flex-row-reverse' : ''}`}
                      >
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                            {msg.userName?.charAt(0).toUpperCase()}
                          </div>
                        </div>

                        {/* Message Content */}
                        <div className={`flex-1 ${user && msg.userId === user.email ? 'text-right' : ''}`}>
                          <div className={`flex items-center gap-2 mb-1 ${user && msg.userId === user.email ? 'justify-end' : ''}`}>
                            <span className="font-bold text-sm text-gray-900 dark:text-white">
                              {msg.userName}
                              {user && msg.userId === user.email && (
                                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full">You</span>
                              )}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTime(msg.createdAt)}
                            </span>
                          </div>

                          {/* Reply indicator */}
                          {msg.replyTo && (
                            <div className={`mb-2 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-xs border-l-4 border-blue-500 ${user && msg.userId === user.email ? 'text-right' : ''}`}>
                              <p className="text-gray-600 dark:text-gray-400 font-medium">
                                Replying to: {msg.replyTo.message?.substring(0, 50)}...
                              </p>
                            </div>
                          )}

                          <div
                            className={`inline-block max-w-[80%] p-4 rounded-2xl shadow-md ${
                              user && msg.userId === user.email
                                ? 'bg-gradient-to-br from-blue-600 to-blue-500 text-white'
                                : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            {/* Preserve newlines and spaces from database */}
                            <div className="text-sm break-words leading-relaxed whitespace-pre-wrap">
                              {msg.message}
                            </div>
                          </div>

                          {/* Action buttons with VIEWS */}
                          {!selectedArchive && (
                            <div className={`flex items-center gap-4 mt-2 ${user && msg.userId === user.email ? 'justify-end' : ''}`}>
                              <button
                                onClick={() => handleLike(msg._id)}
                                disabled={!user}
                                className={`flex items-center gap-1 text-xs font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${
                                  user && msg.likes?.includes(user.email)
                                    ? 'text-blue-600 dark:text-blue-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                                }`}
                              >
                                <ThumbsUp className="w-4 h-4" />
                                <span>{msg.likes?.length || 0}</span>
                              </button>
                              <button
                                onClick={() => setReplyTo(msg)}
                                disabled={!user}
                                className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Reply className="w-4 h-4" />
                                Reply
                              </button>
                              {/* ===== VIEWS COUNT - NOW UPDATED ===== */}
                              <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 cursor-default hover:text-blue-600 transition" title="Message views">
                                <Eye className="w-4 h-4" />
                                {msg.views || 0}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input - Only for logged in users */}
              {user && !selectedArchive && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
                  {error && (
                    <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 rounded-lg text-sm flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  {replyTo && (
                    <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-blue-800 dark:text-blue-300 font-bold flex items-center gap-2">
                          <Reply className="w-4 h-4" />
                          Replying to {replyTo.userName}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate mt-1">
                          {replyTo.message}
                        </p>
                      </div>
                      <button
                        onClick={() => setReplyTo(null)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 p-1"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  <form onSubmit={handleSendMessage} className="flex gap-3">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      disabled={sending}
                      className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      maxLength={500}
                    />
                    <button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 shadow-lg hover:shadow-xl"
                    >
                      {sending ? (
                        <Loader className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                      <span className="hidden sm:inline">Send</span>
                    </button>
                  </form>
                </div>
              )}

              {/* Message Input Prompt for Non-Logged In Users */}
              {!user && !selectedArchive && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                        <Lock className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 dark:text-white">Login to Participate</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Sign in to send messages, reply, and like posts</p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/login')}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition shadow-lg"
                    >
                      Login Now
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Stats Cards - Available to all users */}
            <div className="mt-6 grid grid-cols-3 gap-4">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg text-center hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  <div className="flex justify-center text-blue-600 mb-3">
                    {stat.icon}
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                    {stat.value}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ===== ARCHIVES MODAL - Only for logged in users ===== */}
      {showArchives && user && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowArchives(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <Archive className="w-8 h-8 text-purple-600" />
                Forum Archives
              </h3>
              <button
                onClick={() => setShowArchives(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(80vh-100px)]">
              {archives.length === 0 ? (
                <div className="text-center py-16">
                  <Calendar className="w-20 h-20 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    No Archives Available
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    Archives will be created automatically at the start of each new year
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {archives.map((archive) => (
                    <button
                      key={archive.year}
                      onClick={() => {
                        loadArchive(archive.year);
                        setShowArchives(false);
                      }}
                      className="w-full p-6 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 hover:from-purple-100 hover:to-indigo-100 dark:hover:from-gray-600 dark:hover:to-gray-500 rounded-xl border-2 border-purple-200 dark:border-gray-600 transition-all text-left shadow-md hover:shadow-xl hover:-translate-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                            <Calendar className="w-8 h-8" />
                          </div>
                          <div>
                            <h4 className="font-bold text-2xl text-gray-900 dark:text-white">
                              Year {archive.year}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {archive.messageCount} messages archived
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Archived on
                          </p>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {new Date(archive.archivedDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}